/**
 * Build selectable job journeys for a customer who may have many
 * lead → quote → invoice → order chains.
 */

import {
  invoiceDisplayFrom,
  leadDisplayFrom,
  orderDisplayFrom,
  pickDisplayLabel,
  pickEntityId,
  pickPrimaryInvoice,
  quoteDisplayFrom,
} from "./buildJobJourneySteps.js";

const entityId = (record) => pickEntityId(record?.id, record?._id);

const timestampOf = (record) => {
  const raw =
    record?.updatedAt ||
    record?.createdAt ||
    record?.move_date ||
    record?.moveDate ||
    null;
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
};

const corridorLabel = (lead, quote) => {
  const from = pickDisplayLabel(
    lead?.moving_from,
    lead?.from,
    quote?.pickup?.city,
    quote?.route?.from
  );
  const to = pickDisplayLabel(
    lead?.moving_to,
    lead?.to,
    quote?.delivery?.city,
    quote?.route?.to
  );
  if (from && to) return `${from} → ${to}`;
  return pickDisplayLabel(from, to);
};

/**
 * @param {{
 *   leads?: Array<Object>,
 *   quotes?: Array<Object>,
 *   invoices?: Array<Object>,
 *   orders?: Array<Object>,
 * }} relations
 * @returns {Array<{
 *   key: string,
 *   leadId: string|null,
 *   quoteId: string|null,
 *   invoiceId: string|null,
 *   orderId: string|null,
 *   leadDisplay: string|null,
 *   quoteDisplay: string|null,
 *   invoiceDisplay: string|null,
 *   orderDisplay: string|null,
 *   label: string,
 *   sortAt: number,
 * }>}
 */
export function buildCustomerJourneys(relations = {}) {
  const leads = Array.isArray(relations.leads) ? relations.leads : [];
  const quotes = Array.isArray(relations.quotes) ? relations.quotes : [];
  const invoices = Array.isArray(relations.invoices) ? relations.invoices : [];
  const orders = Array.isArray(relations.orders) ? relations.orders : [];

  const journeys = [];
  const usedQuoteIds = new Set();

  const findOrdersForQuote = (quote) => {
    const quoteId = entityId(quote);
    const orderId = pickEntityId(quote?.orderId, quote?.payment?.orderId);
    return orders.filter((order) => {
      const oid = entityId(order);
      return (
        (quoteId && pickEntityId(order?.quoteId) === quoteId) ||
        (orderId && oid === orderId) ||
        (orderId && pickEntityId(order?.jobId) === orderId)
      );
    });
  };

  const pushJourney = ({ lead = null, quote = null }) => {
    const leadId = entityId(lead);
    const quoteId = entityId(quote);
    if (quoteId) usedQuoteIds.add(quoteId);

    const invoice = quote
      ? pickPrimaryInvoice(invoices, quoteId)
      : null;
    const orderList = quote ? findOrdersForQuote(quote) : [];
    const order = orderList[0] || null;

    const leadDisplay = leadDisplayFrom(lead);
    const quoteDisplay = quoteDisplayFrom(quote);
    const invoiceDisplay = invoiceDisplayFrom(invoice);
    const orderDisplay = orderDisplayFrom(order);
    const corridor = corridorLabel(lead, quote);

    const title = pickDisplayLabel(
      leadDisplay,
      quoteDisplay,
      orderDisplay,
      invoiceDisplay,
      "Journey"
    );
    const label = corridor ? `${title} · ${corridor}` : title;

    journeys.push({
      key: `${leadId || "no-lead"}:${quoteId || "no-quote"}`,
      leadId,
      quoteId,
      invoiceId: entityId(invoice),
      orderId: entityId(order) || pickEntityId(order?.jobId),
      leadDisplay,
      quoteDisplay,
      invoiceDisplay,
      orderDisplay,
      label,
      sortAt: Math.max(
        timestampOf(order),
        timestampOf(invoice),
        timestampOf(quote),
        timestampOf(lead)
      ),
    });
  };

  for (const lead of leads) {
    const leadId = entityId(lead);
    const leadQuotes = quotes.filter(
      (quote) => pickEntityId(quote?.leadId) === leadId
    );

    if (leadQuotes.length === 0) {
      pushJourney({ lead, quote: null });
      continue;
    }

    for (const quote of leadQuotes) {
      pushJourney({ lead, quote });
    }
  }

  for (const quote of quotes) {
    const quoteId = entityId(quote);
    if (!quoteId || usedQuoteIds.has(quoteId)) continue;
    pushJourney({ lead: null, quote });
  }

  journeys.sort((a, b) => b.sortAt - a.sortAt);
  return journeys;
}

/**
 * Pick a journey from deep-link params or default to the newest.
 * @param {Array<Object>} journeys
 * @param {{ leadId?: string|null, quoteId?: string|null }} [selection]
 */
export function selectCustomerJourney(journeys, selection = {}) {
  const list = Array.isArray(journeys) ? journeys : [];
  if (list.length === 0) return null;

  const quoteId = pickEntityId(selection.quoteId);
  const leadId = pickEntityId(selection.leadId);

  if (quoteId) {
    const byQuote = list.find((j) => j.quoteId === quoteId);
    if (byQuote) return byQuote;
  }

  if (leadId) {
    const byLead = list.find((j) => j.leadId === leadId);
    if (byLead) return byLead;
  }

  return list[0];
}

/**
 * Seed JobJourneyStrip links for the customer page + selected journey.
 * @param {Object|null|undefined} customer
 * @param {Object|null|undefined} journey
 */
export function seedFromCustomer(customer, journey = null) {
  return {
    current: "customer",
    customerId: pickEntityId(customer?.id, customer?._id),
    leadId: pickEntityId(journey?.leadId),
    quoteId: pickEntityId(journey?.quoteId),
    invoiceId: pickEntityId(journey?.invoiceId),
    orderId: pickEntityId(journey?.orderId),
    customerDisplay: pickDisplayLabel(customer?.name),
    leadDisplay: pickDisplayLabel(journey?.leadDisplay),
    quoteDisplay: pickDisplayLabel(journey?.quoteDisplay),
    invoiceDisplay: pickDisplayLabel(journey?.invoiceDisplay),
    orderDisplay: pickDisplayLabel(journey?.orderDisplay),
  };
}
